import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { 
  ArrowRight, 
  Shield, 
  Users, 
  CreditCard, 
  DollarSign, 
  TrendingUp,
  Building,
  Smartphone,
  Clock,
  Award,
  CheckCircle,
  Star,
  Quote,
  Play,
  Sparkles,
  Globe,
  Zap
} from 'lucide-react';
import heroVideo from '../assets/heropeople.mp4';

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    {
      icon: CreditCard,
      title: 'Personal Banking',
      description: 'Comprehensive banking solutions tailored to your personal financial needs.',
      features: ['Checking & Savings', 'Credit Cards', 'Personal Loans', 'Mobile Banking'],
      link: '/personal-banking',
      color: 'from-primary-500 to-primary-600'
    },
    {
      icon: Building,
      title: 'Business Banking',
      description: 'Powerful financial tools to help your business grow and succeed.',
      features: ['Business Accounts', 'Commercial Loans', 'Merchant Services', 'Cash Management'],
      link: '/business-banking',
      color: 'from-primary-600 to-primary-700'
    },
    {
      icon: DollarSign,
      title: 'Loans & Mortgages',
      description: 'Finance your dreams with competitive rates.',
      features: ['Home Mortgages', 'Auto Loans', 'Personal Loans', 'Home Equity'],
      link: '/loans-info',
      color: 'from-accent-500 to-accent-600'
    },
    {
      icon: TrendingUp,
      title: 'Investment Services',
      description: 'Build wealth and secure your financial future with our investment solutions.',
      features: ['Retirement Planning', 'Investment Portfolios', 'Financial Advice', 'Wealth Management'],
      link: '/personal-banking#investments',
      color: 'from-accent-500 to-accent-600'
    }
  ];

  const stats = [
    { number: 50000, suffix: '+', label: 'Happy Customers', icon: Users },
    { number: 2.5, suffix: 'B+', prefix: '$', label: 'Assets Under Management', icon: DollarSign },
    { number: 25, suffix: '+', label: 'Branch Locations', icon: Building },
    { number: 99.9, suffix: '%', label: 'Customer Satisfaction', icon: Award }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Small Business Owner',
              content: 'First Capital International has been instrumental in helping my business grow. Their personalized service and competitive rates are unmatched.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108755-2616c96b7852?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Michael Chen',
      role: 'Homeowner',
              content: 'Getting our mortgage through First Capital International was seamless. The team guided us through every step and secured us an excellent rate.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Financial Planner',
              content: 'The investment services at First Capital International have helped me build a solid portfolio. Their financial advisors truly understand my goals.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
    }
  ];

  const features = [
    {
      icon: Smartphone,
      title: '24/7 Mobile Banking',
      description: 'Bank anytime, anywhere with our award-winning mobile app.',
      color: 'from-primary-500 to-primary-600'
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'Your money and data are protected with military-grade encryption.',
      color: 'from-primary-600 to-primary-700'
    },
    {
      icon: Zap,
      title: 'Instant Transfers',
      description: 'Send money instantly to friends, family, or business partners.',
      color: 'from-accent-500 to-accent-600'
    },
    {
      icon: Users,
      title: 'Personal Service',
      description: 'Dedicated relationship managers who know you and your goals.',
      color: 'from-heritage-500 to-primary-500'
    }
  ];

  return (
    <div className="pt-20">
      {/* Enhanced Hero Section with Video Background */}
      <section ref={heroRef} className="relative h-[75vh] lg:h-screen flex items-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          {/* Enhanced overlay with logo-matching colors */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 via-primary-800/80 to-heritage-800/75"></div>
          <div className="absolute inset-0 bg-black/25"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              y: [0, -20, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary-500/10 rounded-full blur-xl"
          />
          <motion.div
            animate={{ 
              y: [0, 30, 0],
              rotate: [0, -5, 0]
            }}
            transition={{ 
              duration: 10, 
              repeat: Infinity, 
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-heritage-500/10 rounded-full blur-2xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Enhanced Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-white lg:py-12"
            >
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 0.6 }}
                className="text-3xl md:text-4xl lg:text-7xl font-black leading-tight mb-6 lg:mb-10"
              >
                Banking Made
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 via-primary-400 to-heritage-400 animate-pulse">
                  Extraordinary
                </span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 0.8 }}
                className="text-base lg:text-2xl text-blue-100 leading-relaxed mb-8 lg:mb-12 max-w-lg font-light"
              >
                Experience the future of banking with First Capital International. 
                We're not just your bank—we're your financial partner for life.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 1 }}
                className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 lg:space-x-8 mb-8 lg:mb-16"
              >
                <Link
                  to="/online-banking"
                  className="group relative overflow-hidden bg-gradient-to-r from-accent-400 to-accent-500 text-black font-bold py-3 lg:py-4 px-6 lg:px-10 rounded-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl text-center"
                >
                  <span className="relative z-10 flex items-center justify-center text-base lg:text-lg">
                    Online Access
                    <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-accent-500 to-accent-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
                
                <Link
                  to="/register"
                  className="group flex items-center justify-center space-x-3 lg:space-x-4 bg-white/10 backdrop-blur-md text-white font-semibold py-3 lg:py-4 px-6 lg:px-10 rounded-2xl border border-white/30 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="w-8 h-8 lg:w-12 lg:h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <CreditCard className="w-4 h-4 lg:w-6 lg:h-6" />
                  </div>
                  <span className="text-base lg:text-lg">Open account</span>
                </Link>
              </motion.div>

              {/* Trust Indicators - Desktop only with better spacing */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 1.2 }}
                className="hidden lg:flex items-center space-x-10"
              >
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-primary-400" />
                  <span className="text-base font-medium">FDIC Insured</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="w-6 h-6 text-heritage-400" />
                  <span className="text-base font-medium">25+ Locations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="w-6 h-6 text-accent-400" />
                  <span className="text-base font-medium">Award Winning</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Enhanced Hero Visual - Desktop only */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Enhanced Banking Card Mockup */}
                <motion.div
                  animate={{ 
                    y: [0, -15, 0],
                    rotateY: [0, 5, 0]
                  }}
                  transition={{ 
                    duration: 6, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-black rounded-3xl p-8 shadow-2xl border border-white/10 backdrop-blur-sm">
                    <div className="flex justify-between items-start mb-8">
                      <div>
                        <div className="text-white/60 text-sm">FIRST CAPITAL</div>
                        <div className="text-white font-bold text-lg">International Bank</div>
                      </div>
                      <div className="w-14 h-10 bg-gradient-to-r from-accent-400 to-accent-500 rounded-lg"></div>
                    </div>
                    
                    <div className="text-white text-3xl font-mono tracking-wider mb-8">
                      •••• •••• •••• 1234
                    </div>
                    
                    <div className="flex justify-between">
                      <div>
                        <div className="text-white/60 text-xs">CARDHOLDER</div>
                        <div className="text-white font-semibold">JOHN CAPITAL</div>
                      </div>
                      <div>
                        <div className="text-white/60 text-xs">EXPIRES</div>
                        <div className="text-white font-semibold">12/28</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating Security Badge */}
                  <motion.div
                    animate={{ 
                      rotate: [0, 360],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 20, 
                      repeat: Infinity, 
                      ease: "linear" 
                    }}
                    className="absolute -top-6 -right-6 w-20 h-20 bg-gradient-to-r from-primary-400 to-heritage-500 rounded-full flex items-center justify-center shadow-2xl"
                  >
                    <Shield className="w-10 h-10 text-white" />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile Card Bridge - Outside hero background */}
      <div className="lg:hidden relative -mt-20 mb-8 px-4 z-30">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.6 }}
          className="relative max-w-sm mx-auto"
        >
          <div className="relative">
            {/* Enhanced Banking Card Mockup */}
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotateY: [0, 3, 0]
              }}
              transition={{ 
                duration: 6, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-black rounded-2xl p-5 shadow-2xl border border-white/10 backdrop-blur-sm">
                <div className="flex justify-between items-start mb-5">
                  <div>
                    <div className="text-white/60 text-xs">FIRST CAPITAL</div>
                    <div className="text-white font-bold text-sm">International Bank</div>
                  </div>
                  <div className="w-10 h-7 bg-gradient-to-r from-accent-400 to-accent-500 rounded-md"></div>
                </div>
                
                <div className="text-white text-xl font-mono tracking-wider mb-5">
                  •••• •••• •••• 1234
                </div>
                
                <div className="flex justify-between">
                  <div>
                    <div className="text-white/60 text-xs">CARDHOLDER</div>
                    <div className="text-white font-semibold text-sm">JOHN CAPITAL</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-xs">EXPIRES</div>
                    <div className="text-white font-semibold text-sm">12/28</div>
                  </div>
                </div>
              </div>
              
              {/* Floating Security Badge */}
              <motion.div
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 20, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
                className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-primary-400 to-heritage-500 rounded-full flex items-center justify-center shadow-2xl"
              >
                <Shield className="w-8 h-8 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Company Cards Section */}
      <section className="py-16 bg-gradient-to-br from-white to-primary-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(0,102,204,0.08),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-4">
              Your Financial Partner
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                Since 1985
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                              Discover why thousands of families and businesses choose First Capital International for their financial journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Trust & Heritage */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-primary-100 group-hover:border-primary-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-heritage-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Trusted Since 1985</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Four decades of unwavering commitment to our community, building lasting relationships and financial security.
                  </p>
                  <Link 
                    to="/about"
                    className="flex items-center text-primary-600 font-semibold group-hover:translate-x-2 transition-transform duration-300 hover:text-primary-700"
                  >
                    <span>Learn Our Story</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Card 2: Innovation */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-heritage-100 group-hover:border-heritage-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-heritage-500/10 to-primary-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-r from-heritage-500 to-heritage-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Modern Technology</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Cutting-edge digital banking solutions that make managing your finances simple, secure, and convenient.
                  </p>
                  <Link 
                    to="/personal-banking"
                    className="flex items-center text-heritage-600 font-semibold group-hover:translate-x-2 transition-transform duration-300 hover:text-heritage-700"
                  >
                    <span>Explore Digital Banking</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Card 3: Community */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-accent-100 group-hover:border-accent-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-500/10 to-primary-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Community Focused</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Deeply rooted in our community, supporting local businesses and families with personalized financial solutions.
                  </p>
                  <Link 
                    to="/about"
                    className="flex items-center text-accent-600 font-semibold group-hover:translate-x-2 transition-transform duration-300 hover:text-accent-700"
                  >
                    <span>Community Impact</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section ref={statsRef} className="py-20 bg-gradient-to-br from-primary-50 to-heritage-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,102,204,0.08),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={statsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6">
              Trusted by Numbers
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                That Matter
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Our commitment to excellence is reflected in every metric that shows our dedication to our community and customers.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                animate={statsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                className="text-center group"
              >
                <div className="relative">
                  <div className={`w-20 h-20 lg:w-24 lg:h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 shadow-xl ${
                    index % 3 === 0 ? 'bg-gradient-to-br from-primary-500 to-primary-700' :
                    index % 3 === 1 ? 'bg-gradient-to-br from-heritage-500 to-heritage-700' :
                    'bg-gradient-to-br from-accent-500 to-accent-700'
                  }`}>
                    <stat.icon className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
                  </div>
                  <div className={`absolute inset-0 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-300 ${
                    index % 3 === 0 ? 'bg-gradient-to-br from-primary-400 to-primary-600' :
                    index % 3 === 1 ? 'bg-gradient-to-br from-heritage-400 to-heritage-600' :
                    'bg-gradient-to-br from-accent-400 to-accent-600'
                  }`}></div>
                </div>
                
                <div className="text-4xl lg:text-5xl font-black text-slate-900 mb-3">
                  {statsInView && (
                    <>
                      {stat.prefix}
                      <CountUp end={stat.number} duration={3} decimals={stat.number % 1 !== 0 ? 1 : 0} />
                      {stat.suffix}
                    </>
                  )}
                </div>
                <div className="text-slate-600 font-semibold text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Services Section */}
      <section ref={servicesRef} className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(220,38,38,0.05),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6">
              Complete Banking
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              From personal banking to business solutions, we offer a complete range of financial services 
              designed to meet your unique needs and help you achieve your goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group"
              >
                <div className="relative h-full">
                  <div className="card p-8 h-full group-hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-primary-200 bg-white rounded-2xl">
                    <div className="relative">
                      <div className={`w-20 h-20 bg-gradient-to-r ${service.color} rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                        <service.icon className="w-10 h-10 text-white" />
                      </div>
                      <div className={`absolute inset-0 bg-gradient-to-r ${service.color} rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-primary-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                    
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li 
                          key={feature} 
                          initial={{ opacity: 0, x: -20 }}
                          animate={servicesInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: (index * 0.1) + (featureIndex * 0.05) }}
                          className="flex items-center space-x-3"
                        >
                          <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                    
                    <Link
                      to={service.link}
                      className="inline-flex items-center space-x-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors group-hover:translate-x-2 transform duration-300"
                    >
                      <span>Explore Services</span>
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(0,102,204,0.08),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <motion.h2 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl lg:text-5xl font-black text-slate-900 mb-8"
              >
                Why Choose
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                  First Capital?
                </span>
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl text-slate-600 mb-12 leading-relaxed"
              >
                We combine cutting-edge technology with personalized service to deliver 
                an exceptional banking experience that puts your needs first.
              </motion.p>
              
              <div className="space-y-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.15 }}
                    className="flex items-start space-x-6 group"
                  >
                    <div className="relative">
                      <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                        <feature.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-primary-700 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10"
              >
                <img
                  src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=600&h=600&fit=crop"
                  alt="Modern Banking Technology"
                  className="rounded-3xl shadow-2xl"
                />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-accent-600/20 rounded-3xl blur-xl"></div>
              
              {/* Floating Elements */}
              <motion.div
                animate={{ 
                  y: [0, -30, 0],
                  rotate: [0, 10, 0]
                }}
                transition={{ 
                  duration: 8, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: 1
                }}
                className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-r from-accent-400 to-accent-500 rounded-full flex items-center justify-center shadow-2xl"
              >
                <Sparkles className="w-12 h-12 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-primary-900 to-heritage-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,102,204,0.1),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
              What Our Customers
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
                Are Saying
              </span>
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                              Don't just take our word for it. Hear from real customers about their experience with First Capital International.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                className="group"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 relative border border-white/20 group-hover:bg-white/15 transition-all duration-500 group-hover:scale-105">
                  <Quote className="w-10 h-10 text-accent-400 mb-6" />
                  
                  <p className="text-white mb-8 leading-relaxed text-lg">
                    "{testimonial.content}"
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
                    />
                    <div>
                      <div className="font-bold text-white text-lg">{testimonial.name}</div>
                      <div className="text-blue-200">{testimonial.role}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 mt-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-accent-400 fill-current" />
                    ))}
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-r from-accent-400/20 to-accent-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-heritage-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.1),transparent)]"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="w-20 h-20 bg-gradient-to-r from-accent-400 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <Sparkles className="w-10 h-10 text-white" />
            </motion.div>
            
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-8">
              Ready to Experience
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
                Better Banking?
              </span>
            </h2>
            <p className="text-xl text-blue-100 mb-12 leading-relaxed">
                              Join thousands of satisfied customers who trust First Capital International 
              for all their financial needs. Open your account today and discover the difference.
            </p>
            
            <div className="flex flex-col sm:flex-row space-y-6 sm:space-y-0 sm:space-x-6 justify-center">
              <Link
                to="/register"
                className="group relative overflow-hidden bg-gradient-to-r from-accent-400 to-accent-500 text-black font-bold py-4 px-10 rounded-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Open account
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-accent-500 to-accent-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              <Link
                to="/contact"
                className="flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md text-white font-semibold py-4 px-10 rounded-2xl border border-white/30 hover:bg-white/20 transition-all duration-300 group"
              >
                <span>Find a Branch</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* News & Updates Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(0,102,204,0.05),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6">
              Latest News &
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                Updates
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                              Stay informed about the latest developments, community events, and financial insights from First Capital International.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.article
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="group"
            >
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop" 
                  alt="Community Event"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="p-6">
                  <div className="text-sm text-primary-600 font-semibold mb-2">COMMUNITY NEWS</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-primary-700 transition-colors">
                    Annual Community Day Success
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Our 2024 Community Day raised over $25,000 for local charities, bringing together families and businesses.
                  </p>
                  <div className="flex items-center text-primary-600 font-semibold">
                    <span>Read More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="group"
            >
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop" 
                  alt="Digital Banking"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="p-6">
                  <div className="text-sm text-heritage-600 font-semibold mb-2">PRODUCT UPDATE</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-heritage-700 transition-colors">
                    New Mobile App Features
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Enhanced security features and instant money transfers now available in our award-winning mobile app.
                  </p>
                  <div className="flex items-center text-heritage-600 font-semibold">
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="group"
            >
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=250&fit=crop" 
                  alt="Financial Planning"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="p-6">
                  <div className="text-sm text-accent-600 font-semibold mb-2">FINANCIAL INSIGHTS</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-accent-700 transition-colors">
                    2025 Investment Outlook
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Our financial experts share insights on investment opportunities and market trends for the coming year.
                  </p>
                  <div className="flex items-center text-accent-600 font-semibold">
                    <span>Read Analysis</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.article>
          </div>

          <div className="text-center">
            <Link
              to="/about"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-heritage-600 text-white font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300 group"
            >
              <span>View All News</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Community Impact Section */}
      <section className="py-20 bg-gradient-to-br from-heritage-50 to-primary-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(220,38,38,0.08),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-8">
                Making a Difference in
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-heritage-600 to-primary-600">
                  Our Community
                </span>
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                For nearly four decades, we've been more than just a bank. We're active partners 
                in building stronger communities through local investments, charitable giving, and volunteer work.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-heritage-500 to-heritage-600 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-lg">$2.5M+ Donated</div>
                    <div className="text-slate-600">To local charities and causes</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-lg">500+ Volunteer Hours</div>
                    <div className="text-slate-600">By our team members annually</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-lg">1,200+ Local Businesses</div>
                    <div className="text-slate-600">Supported with loans and services</div>
                  </div>
                </div>
              </div>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-heritage-600 to-primary-600 text-white font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300 group"
              >
                <span>Our Community Story</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.img
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=300&h=200&fit=crop"
                  alt="Community Event"
                  className="rounded-2xl shadow-lg"
                />
                <motion.img
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=300&h=200&fit=crop"
                  alt="Local Business"
                  className="rounded-2xl shadow-lg mt-8"
                />
                <motion.img
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                  src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop"
                  alt="Charity Work"
                  className="rounded-2xl shadow-lg -mt-4"
                />
                <motion.img
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                  src="https://images.unsplash.com/photo-1515378791036-0648a814c963?w=300&h=200&fit=crop"
                  alt="Community Support"
                  className="rounded-2xl shadow-lg mt-4"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-heritage-600/10 to-primary-600/10 rounded-3xl blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security & Protection Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,102,204,0.1),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
              Your Security is
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
                Our Priority
              </span>
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Advanced security measures and fraud protection ensure your money and personal information are always safe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: "FDIC Insured",
                description: "Your deposits are protected up to $250,000 by federal insurance.",
                color: "from-primary-500 to-primary-600"
              },
              {
                icon: Clock,
                title: "24/7 Monitoring",
                description: "Round-the-clock fraud detection and account monitoring systems.",
                color: "from-heritage-500 to-heritage-600"
              },
              {
                icon: Smartphone,
                title: "Secure Mobile App",
                description: "Biometric login and end-to-end encryption for mobile banking.",
                color: "from-accent-500 to-accent-600"
              },
              {
                icon: CheckCircle,
                title: "Zero Liability",
                description: "You're not responsible for unauthorized transactions when reported promptly.",
                color: "from-primary-500 to-primary-600"
              }
            ].map((security, index) => (
              <motion.div
                key={security.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 group-hover:bg-white/15 transition-all duration-500 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-r ${security.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all duration-300`}>
                    <security.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{security.title}</h3>
                  <p className="text-blue-200">{security.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/about"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent-400 to-accent-500 text-black font-bold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300 group"
            >
              <span>Learn About Our Security</span>
              <Shield className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Digital Banking Features Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(0,102,204,0.08),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6">
              Banking at Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-heritage-600">
                Fingertips
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Experience the convenience of modern digital banking with features designed to make your financial life easier.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-8">
                {[
                  {
                    icon: Smartphone,
                    title: "Mobile Check Deposit",
                    description: "Deposit checks instantly by taking a photo with your smartphone."
                  },
                  {
                    icon: Zap,
                    title: "Instant Transfers",
                    description: "Send money to family and friends instantly with Zelle® and wire transfers."
                  },
                  {
                    icon: CreditCard,
                    title: "Digital Wallet Support",
                    description: "Use Apple Pay, Google Pay, and Samsung Pay for secure contactless payments."
                  },
                  {
                    icon: TrendingUp,
                    title: "Financial Insights",
                    description: "Track spending, set budgets, and get personalized financial insights."
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-4 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-heritage-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-all duration-300">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                      <p className="text-slate-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link
                  to="/online-banking"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-heritage-600 text-white font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300 group"
                >
                  <span>Try Digital Banking</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <img
                  src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=600&h=600&fit=crop"
                  alt="Mobile Banking App"
                  className="rounded-3xl shadow-2xl"
                />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-heritage-600/20 rounded-3xl blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Account Types Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(220,38,38,0.05),transparent)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6">
              Find the Perfect
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-heritage-600 to-primary-600">
                Account for You
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Whether you're saving for the future, managing daily expenses, or growing your business, we have the right account.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: "High-Yield Savings",
                rate: "4.25% APY",
                features: ["No minimum balance", "Monthly interest payments", "Free online banking", "Mobile app access"],
                color: "from-primary-500 to-primary-600",
                link: "/personal-banking"
              },
              {
                icon: CreditCard,
                title: "Premium Checking",
                rate: "No Fees",
                features: ["Free ATM withdrawals", "Overdraft protection", "Debit card rewards", "Bill pay service"],
                color: "from-heritage-500 to-heritage-600",
                link: "/personal-banking"
              },
              {
                icon: TrendingUp,
                title: "Investment Account",
                rate: "Portfolio Growth",
                features: ["Professional management", "Diversified portfolio", "Retirement planning", "Tax advantages"],
                color: "from-accent-500 to-accent-600",
                link: "/personal-banking"
              }
            ].map((account, index) => (
              <motion.div
                key={account.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-primary-200 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/5 to-heritage-500/5 rounded-full blur-2xl"></div>
                  
                  <div className="relative">
                    <div className={`w-16 h-16 bg-gradient-to-r ${account.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300`}>
                      <account.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{account.title}</h3>
                    <div className="text-3xl font-black text-primary-600 mb-6">{account.rate}</div>
                    
                    <ul className="space-y-3 mb-8">
                      {account.features.map((feature, featureIndex) => (
                        <li key={feature} className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link
                      to="/register"
                      className="w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-primary-600 to-heritage-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300 group"
                    >
                      <span>Open account</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 